import { termMaxConfigByChain } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import {
  fetchTermMaxMarkets,
  getCachedTermMaxMarkets,
} from '../../public-data/termmax/fetchPublic'
import type { TermMaxMarketConfig } from '../../public-data/termmax/types'
import { getTermMaxMaturedResidue } from '../../public-data/termmax/maturedRoster'

/**
 * TermMax user data is a DISCOVERY-first, async build — the Teller/Liquity
 * shape, not the Midnight one.
 *
 * Midnight can build synchronously because its positions are keyed
 * `(marketId, user)`: one fixed slot per user per market, with the market list
 * coming from a static registry. TermMax has neither property:
 *
 *  1. the market list is DYNAMIC (markets churn on every maturity roll and
 *     matured ones vanish upstream), so it must be resolved at call time; and
 *  2. borrow positions are GT ERC-721 sub-accounts — a user can hold N per
 *     market and the ids are not derivable.
 *
 * But it is cheaper than Teller: `TermMaxViewer.getPositionDetails(markets[],
 * owner)` returns FT/XT/collateral balances AND every GT with its debt and
 * collateral in ONE call, so discovery and data collapse into a single
 * multicall entry — no two-phase discovery round-trip. The viewer `try`-guards
 * each position internally, so one bad loan cannot poison the batch.
 *
 * Health is NOT read per GT (`getLiquidationInfo`): it is computed downstream
 * from LTVs + oracle prices in `createMultiAccountTypeUserState`, exactly as
 * Midnight does. That keeps this to one call.
 */

/**
 * Markets per `getPositionDetails` call.
 *
 * Measured against the live Ethereum viewer: 200 markets answer cleanly (44.9 KB
 * of return data), 706 in one array REVERTS on gas. Matured markets are not the
 * problem — a market matured 2025-04-02 reads fine on its own — the ARRAY SIZE is.
 * 180 leaves headroom under the observed ceiling.
 */
export const TERMMAX_MARKETS_PER_CALL = 180

const CACHE_TTL_MS = 5 * 60_000

export interface TermMaxDiscovery {
  /** Markets passed to the viewer, IN ORDER — the parser slices results by index. */
  markets: TermMaxMarketConfig[]
  /** How many `getPositionDetails` calls the roster was split across. */
  chunks: number
  at: number
}

const discoveryCache = new Map<string, TermMaxDiscovery>()
const cacheKey = (chainId: string, account: string) =>
  `${chainId}:${account.toLowerCase()}`

/**
 * The market layout used for the last build on this (chain, account).
 *
 * The parser runs as a separate phase, fed only the multicall results, so it
 * needs the layout the builder chose — the same trick the Teller / Liquity /
 * Lista-broker caches use.
 */
export const getCachedTermMaxDiscovery = (
  chainId: string,
  account: string,
): TermMaxDiscovery | undefined => {
  const hit = discoveryCache.get(cacheKey(chainId, account))
  if (!hit || Date.now() - hit.at > CACHE_TTL_MS) return undefined
  return hit
}

/** Test seam. */
export const __resetTermMaxDiscovery = () => discoveryCache.clear()

/**
 * Build the user-data call for every live TermMax market on a chain.
 *
 * Returns `[]` when the chain has no TermMax deployment, no viewer configured,
 * or no live markets — callers can fan out unconditionally.
 */
export const buildTermMaxUserCall = async (
  chainId: string,
  _lender: string,
  account: string,
): Promise<Call[]> => {
  const proto = termMaxConfigByChain(chainId)
  if (!proto?.viewer) return []
  let live = getCachedTermMaxMarkets(chainId)
  if (live.length === 0) {
    try {
      live = (await fetchTermMaxMarkets(chainId)).map((m) => m.config)
    } catch {
      live = []
    }
  }

  /**
   * MATURED MARKETS ARE PART OF A USER'S POSITION SET.
   *
   * `fetchTermMaxMarkets` is live-only, which is correct for the public surface
   * and wrong here: FT still redeems after maturity and a GT still carries an
   * obligation, so reading only live markets makes anyone who never came back
   * invisible. The residue sweep is what keeps that bounded — it drops the
   * matured markets that are fully settled, which on Ethereum is 683 → 306.
   *
   * NON-BLOCKING on purpose. The residue is chain-scoped and shared by every
   * user, so it is refreshed in the background and read from cache here: a cold
   * cache returns empty and this read degrades to live-only rather than waiting
   * on a third-party API that has been measured at 13 s.
   */
  const matured = getTermMaxMaturedResidue(chainId)

  const seen = new Set<string>()
  const markets = [...live, ...matured].filter((m) => {
    const k = m.market.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })

  if (markets.length === 0) {
    discoveryCache.set(cacheKey(chainId, account), {
      markets: [],
      chunks: 0,
      at: Date.now(),
    })
    return []
  }

  markets.sort((a, b) => a.market.localeCompare(b.market))

  // Disjoint arrays rather than one: the viewer refuses a roster this size in a
  // single call. They are still ONE RPC request — separate entries in the same
  // multicall — so the split costs calldata and gas, never a round trip.
  const calls: Call[] = []
  for (let i = 0; i < markets.length; i += TERMMAX_MARKETS_PER_CALL) {
    calls.push({
      address: proto.viewer,
      name: 'getPositionDetails',
      params: [
        markets
          .slice(i, i + TERMMAX_MARKETS_PER_CALL)
          .map((m) => m.market),
        account,
      ],
    })
  }

  discoveryCache.set(cacheKey(chainId, account), {
    markets,
    chunks: calls.length,
    at: Date.now(),
  })
  return calls
}
